import { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  TableCellsIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

export default function SchemaEditor({ dataSource, currentSchema, onSave, onClose, isSaving }) {
  const [isLoading, setIsLoading] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  
  // MongoDB filter state
  const isMongoDB = dataSource.type === 'mongodb';
  const [importFilter, setImportFilter] = useState('');
  const [filterError, setFilterError] = useState(null);
  
  // MongoDB flattening config state
  const [flattenConfig, setFlattenConfig] = useState({
    maxDepth: 3,
    arrayHandling: 'first',
    separator: '.'
  });
  
  // SQL query state (PostgreSQL/MySQL)
  const isSQL = dataSource.type === 'postgresql' || dataSource.type === 'mysql';
  const [sqlQuery, setSqlQuery] = useState('');
  const [sqlQueryError, setSqlQueryError] = useState(null);
  const currentSqlQuery = dataSource.connectionConfig?.sqlQuery;
  
  // Get currently selected columns from the data source
  const currentColumns = dataSource.connectionConfig?.columns || [];
  const currentFilter = dataSource.connectionConfig?.importFilter;
  const currentFlattenConfig = dataSource.connectionConfig?.flattenConfig;
  
  // Initialize selected columns, filter, and flatten config from current config
  useEffect(() => {
    setSelectedColumns(new Set(currentColumns));
    // Initialize filter
    if (currentFilter) {
      const filterStr = typeof currentFilter === 'string' 
        ? currentFilter 
        : JSON.stringify(currentFilter, null, 2);
      setImportFilter(filterStr);
    }
    // Initialize flatten config (with backwards compatibility for 'expand')
    if (currentFlattenConfig) {
      setFlattenConfig({
        maxDepth: currentFlattenConfig.maxDepth || 3,
        // Map 'expand' to 'stringify' for backwards compatibility
        arrayHandling: currentFlattenConfig.arrayHandling === 'expand' 
          ? 'stringify' 
          : (currentFlattenConfig.arrayHandling || 'first'),
        separator: currentFlattenConfig.separator || '.'
      });
    }
    // Initialize SQL query
    if (currentSqlQuery) {
      setSqlQuery(currentSqlQuery);
    }
  }, []);
  
  // Fetch available columns from the source database
  useEffect(() => {
    fetchAvailableColumns();
  }, []);
  
  const fetchAvailableColumns = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Use the backend endpoint that has access to stored credentials
      const response = await api.get(`/data-sources/${dataSource._id}/available-columns`);
      
      // Get columns from the response
      const columns = response.data.columns || [];
      setAvailableColumns(columns);
      
      // If we have current columns, keep them selected, otherwise select all
      if (currentColumns.length > 0) {
        setSelectedColumns(new Set(currentColumns));
      } else {
        setSelectedColumns(new Set(columns.map(c => c.name)));
      }
    } catch (err) {
      console.error('Failed to fetch columns:', err);
      const errorMessage = err.response?.data?.error || 'Failed to connect to the data source.';
      setError(`${errorMessage} Using existing schema.`);
      
      // Fall back to current schema columns
      const existingColumns = currentSchema?.tables?.[0]?.columns || [];
      setAvailableColumns(existingColumns);
      setSelectedColumns(new Set(currentColumns.length > 0 ? currentColumns : existingColumns.map(c => c.name)));
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleColumn = (columnName) => {
    const updated = new Set(selectedColumns);
    if (updated.has(columnName)) {
      updated.delete(columnName);
    } else {
      updated.add(columnName);
    }
    setSelectedColumns(updated);
  };
  
  const selectAll = () => {
    setSelectedColumns(new Set(availableColumns.map(c => c.name)));
  };
  
  const selectNone = () => {
    setSelectedColumns(new Set());
  };
  
  // Validate MongoDB filter JSON
  const validateFilter = (filterStr) => {
    if (!filterStr || filterStr.trim() === '') {
      setFilterError(null);
      return true;
    }
    try {
      const parsed = JSON.parse(filterStr);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        setFilterError('Filter must be a JSON object');
        return false;
      }
      setFilterError(null);
      return true;
    } catch (e) {
      setFilterError('Invalid JSON format');
      return false;
    }
  };
  
  const handleFilterChange = (value) => {
    setImportFilter(value);
    validateFilter(value);
  };
  
  // Validate SQL query (basic validation)
  const validateSqlQuery = (query) => {
    if (!query || query.trim() === '') {
      setSqlQueryError(null);
      return true;
    }
    const trimmed = query.trim().toLowerCase();
    // Must start with SELECT
    if (!trimmed.startsWith('select')) {
      setSqlQueryError('Query must start with SELECT');
      return false;
    }
    // Basic SQL injection protection - no semicolons (prevents multiple statements)
    if (query.includes(';') && query.trim().indexOf(';') < query.trim().length - 1) {
      setSqlQueryError('Multiple statements are not allowed');
      return false;
    }
    // No dangerous keywords
    const dangerousKeywords = ['drop ', 'delete ', 'update ', 'insert ', 'truncate ', 'alter ', 'create ', 'grant ', 'revoke '];
    for (const keyword of dangerousKeywords) {
      if (trimmed.includes(keyword)) {
        setSqlQueryError(`Queries containing "${keyword.trim().toUpperCase()}" are not allowed`);
        return false;
      }
    }
    setSqlQueryError(null);
    return true;
  };
  
  const handleSqlQueryChange = (value) => {
    setSqlQuery(value);
    validateSqlQuery(value);
  };
  
  const handleSave = () => {
    if (selectedColumns.size === 0) {
      toast.error('Please select at least one column');
      return;
    }
    
    // Validate filter if MongoDB
    if (isMongoDB && importFilter && !validateFilter(importFilter)) {
      toast.error('Please fix the filter JSON before saving');
      return;
    }
    
    // Validate SQL query if SQL database
    if (isSQL && sqlQuery && !validateSqlQuery(sqlQuery)) {
      toast.error('Please fix the SQL query before saving');
      return;
    }
    
    // Parse filter for MongoDB
    let parsedFilter = null;
    if (isMongoDB && importFilter && importFilter.trim()) {
      try {
        parsedFilter = JSON.parse(importFilter);
      } catch (e) {
        toast.error('Invalid filter JSON');
        return;
      }
    }
    
    // Update the connectionConfig with new columns, filter, and flatten config
    const updatedConfig = {
      ...dataSource.connectionConfig,
      columns: Array.from(selectedColumns),
      ...(isMongoDB && { 
        importFilter: parsedFilter,
        flattenConfig: flattenConfig
      }),
      ...(isSQL && { 
        sqlQuery: sqlQuery.trim() || null
      })
    };
    
    onSave({ connectionConfig: updatedConfig });
  };
  
  // Filter columns based on search
  const filteredColumns = availableColumns.filter(col => 
    col.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Check if columns, filter, or flatten config have changed
  const hasChanges = () => {
    // Check column changes
    if (currentColumns.length !== selectedColumns.size) return true;
    if (currentColumns.some(col => !selectedColumns.has(col))) return true;
    
    // Check filter and flatten config changes for MongoDB
    if (isMongoDB) {
      const currentFilterStr = currentFilter 
        ? (typeof currentFilter === 'string' ? currentFilter : JSON.stringify(currentFilter))
        : '';
      const newFilterStr = importFilter.trim();
      
      // Compare normalized JSON strings
      try {
        const currentParsed = currentFilterStr ? JSON.parse(currentFilterStr) : null;
        const newParsed = newFilterStr ? JSON.parse(newFilterStr) : null;
        if (JSON.stringify(currentParsed) !== JSON.stringify(newParsed)) return true;
      } catch (e) {
        // If parsing fails, do simple string comparison
        if (currentFilterStr !== newFilterStr) return true;
      }
      
      // Check flatten config changes
      if (currentFlattenConfig) {
        const currentArrayHandling = currentFlattenConfig.arrayHandling === 'expand' 
          ? 'stringify' 
          : currentFlattenConfig.arrayHandling;
        if (currentFlattenConfig.maxDepth !== flattenConfig.maxDepth) return true;
        if (currentArrayHandling !== flattenConfig.arrayHandling) return true;
        if (currentFlattenConfig.separator !== flattenConfig.separator) return true;
      }
    }
    
    // Check SQL query changes for PostgreSQL/MySQL
    if (isSQL) {
      const currentQueryStr = currentSqlQuery || '';
      const newQueryStr = sqlQuery.trim();
      if (currentQueryStr !== newQueryStr) return true;
    }
    
    return false;
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-surface-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-surface-200 dark:border-surface-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-surface-900 dark:text-white flex items-center gap-2">
              <TableCellsIcon className="w-6 h-6" />
              Edit Schema
            </h2>
            <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
              Select which columns to include in {dataSource.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <XMarkIcon className="w-5 h-5 text-surface-500" />
          </button>
        </div>
        
        {/* Warning banner */}
        <div className="px-6 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              After saving changes, you'll need to <strong>sync the data source</strong> to update the data in BigQuery.
              Removing columns will delete that data from BigQuery.
            </p>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-500" />
              <span className="ml-3 text-surface-600 dark:text-surface-400">
                Connecting to data source...
              </span>
            </div>
          ) : error ? (
            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-amber-700 dark:text-amber-300 text-sm">{error}</p>
              <button
                onClick={fetchAvailableColumns}
                className="mt-2 text-sm text-amber-700 dark:text-amber-300 hover:underline flex items-center gap-1"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Retry connection
              </button>
            </div>
          ) : null}
          
          {/* MongoDB Import Filter */}
          {isMongoDB && (
            <div className="mb-6 p-4 border border-surface-200 dark:border-surface-700 rounded-lg bg-surface-50 dark:bg-surface-800/50">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Import Filter
                </h4>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                  Optional
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  MongoDB Query Filter (JSON)
                </label>
                <textarea
                  value={importFilter}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  className={`input font-mono text-sm h-20 ${filterError ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder='{"status": "active", "projectId": {"$oid": "..."}}'
                />
                {filterError && (
                  <p className="text-sm text-red-500 mt-1">{filterError}</p>
                )}
                <div className="text-xs text-surface-500 mt-2 space-y-1">
                  <p>Use MongoDB Extended JSON syntax. For ObjectIds use: <code className="bg-surface-200 dark:bg-surface-700 px-1 rounded">{`{"$oid": "..."}`}</code></p>
                  <p>For dates use: <code className="bg-surface-200 dark:bg-surface-700 px-1 rounded">{`{"$date": "2024-01-01"}`}</code></p>
                </div>
              </div>
            </div>
          )}
          
          {/* MongoDB Flattening Options */}
          {isMongoDB && (
            <div className="mb-6 p-4 border border-surface-200 dark:border-surface-700 rounded-lg bg-surface-50 dark:bg-surface-800/50">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Flattening Options
                </h4>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Max Depth
                  </label>
                  <input
                    type="number"
                    value={flattenConfig.maxDepth}
                    onChange={(e) => setFlattenConfig({ ...flattenConfig, maxDepth: parseInt(e.target.value) || 3 })}
                    className="input"
                    min={1}
                    max={10}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Array Handling
                  </label>
                  <select
                    value={flattenConfig.arrayHandling}
                    onChange={(e) => setFlattenConfig({ ...flattenConfig, arrayHandling: e.target.value })}
                    className="input"
                  >
                    <option value="first">Take first element</option>
                    <option value="stringify">Convert to JSON</option>
                    <option value="join">Join with comma</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Separator
                  </label>
                  <input
                    type="text"
                    value={flattenConfig.separator}
                    onChange={(e) => setFlattenConfig({ ...flattenConfig, separator: e.target.value || '.' })}
                    className="input"
                    placeholder="."
                  />
                </div>
              </div>
              <p className="text-xs text-surface-500 mt-2">
                Changes will take effect on the next sync.
              </p>
            </div>
          )}
          
          {/* SQL Query Editor (PostgreSQL/MySQL) */}
          {isSQL && (
            <div className="mb-6 p-4 border border-surface-200 dark:border-surface-700 rounded-lg bg-surface-50 dark:bg-surface-800/50">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Custom SQL Query
                </h4>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                  Optional
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  SQL Query
                </label>
                <textarea
                  value={sqlQuery}
                  onChange={(e) => handleSqlQueryChange(e.target.value)}
                  className={`input font-mono text-sm h-28 ${sqlQueryError ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder={`SELECT *\nFROM ${dataSource.connectionConfig?.table || 'your_table'}\nWHERE status = 'active'`}
                />
                {sqlQueryError && (
                  <p className="text-sm text-red-500 mt-1">{sqlQueryError}</p>
                )}
                <div className="text-xs text-surface-500 mt-2 space-y-1">
                  <p>Write a custom SELECT query to filter or transform data during import.</p>
                  <p>Only SELECT queries are allowed. Leave empty to import all data from the table.</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Search and actions */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search columns..."
                className="input pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={selectAll}
                className="btn-secondary text-sm"
              >
                Select All
              </button>
              <button
                onClick={selectNone}
                className="btn-secondary text-sm"
              >
                Select None
              </button>
            </div>
          </div>
          
          {/* Column stats */}
          <div className="flex items-center justify-between mb-4 text-sm text-surface-600 dark:text-surface-400">
            <span>{selectedColumns.size} of {availableColumns.length} columns selected</span>
            {hasChanges() && (
              <span className="text-primary-600 dark:text-primary-400 font-medium">
                • Unsaved changes
              </span>
            )}
          </div>
          
          {/* Columns list */}
          <div className="space-y-1 max-h-[400px] overflow-y-auto border border-surface-200 dark:border-surface-700 rounded-lg">
            {filteredColumns.length === 0 ? (
              <div className="p-8 text-center text-surface-500">
                {searchTerm ? 'No columns match your search' : 'No columns available'}
              </div>
            ) : (
              filteredColumns.map((col) => {
                const isSelected = selectedColumns.has(col.name);
                const isNew = !currentColumns.includes(col.name) && isSelected;
                const isRemoved = currentColumns.includes(col.name) && !isSelected;
                
                return (
                  <div
                    key={col.name}
                    onClick={() => toggleColumn(col.name)}
                    className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-primary-50 dark:bg-primary-900/20'
                        : 'hover:bg-surface-50 dark:hover:bg-surface-800'
                    } ${
                      isNew ? 'border-l-4 border-l-green-500' :
                      isRemoved ? 'border-l-4 border-l-red-500' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-surface-300 dark:border-surface-600'
                      }`}>
                        {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <span className="font-mono text-sm text-surface-900 dark:text-white">
                          {col.name}
                        </span>
                        {isNew && (
                          <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-medium">
                            (new)
                          </span>
                        )}
                        {isRemoved && (
                          <span className="ml-2 text-xs text-red-600 dark:text-red-400 font-medium">
                            (will be removed)
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="badge-primary text-xs">{col.type}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-surface-200 dark:border-surface-700 flex items-center justify-between">
          <div className="text-sm text-surface-500">
            Table: <span className="font-mono">{dataSource.connectionConfig?.table}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || selectedColumns.size === 0 || !hasChanges()}
              className="btn-primary disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

